import {
  COMMON_CURRENCY_PRECISION,
  CUSTOMER_STATUSES,
  CUSTOMER_TYPE,
  PAYMENT_METHOD,
  PAYMENT_PROVIDERS,
  TRANSACTION_STATUS,
  TRANSACTION_TYPES,
  TRANSFER_TYPE
} from 'components/constants';
import moment from 'moment-timezone';
import momentLib from 'moment';
import i18n from 'i18n';

// Strips spaces, parentheses and dashes so the phone is stored as +digits (Wallester rejects other formats).
export const normalizePhone = (phone) => (phone ? phone.replace(/[\s()-]/g, '') : phone);

export const emailRegExp =
  // eslint-disable-next-line max-len
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
// Stricter, whitelist-based email check (ASCII-only local part, no /\,;:"<>()[], domain requires a dot + TLD).
// Kept separate from emailRegExp above (which allows a non-ASCII local part) to avoid changing behaviour
// of the other forms already using it.
export const strictEmailRegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;
// eslint-disable-next-line no-useless-escape
export const passwordSpecialSymbolsRegExp = /[!\"#$%&'()*+,-.:;<=>?@\[\]^_`{/|\\}~]/;
export const noNumbersRegExp = /^[^\d]*$/;
export const noCyrillicRegExp = /^[^\u0400-\u04FF]*$/;
export const messageRegExp = /^[^\u0400-\u04FF\u0500-\u052F]*$/;

export const convertBytesToMegabytes = (bytes) => bytes / (1024 * 1024);

export const getCustomerLink = (customerNumber, customerStatus, customerType) => {
  return `/${customerStatus === CUSTOMER_STATUSES.VERIFIED ? 'active-customers' : 'customers'}/${
    customerType === CUSTOMER_TYPE.INDIVIDUAL ? 'individual' : 'corporative'
  }/${customerNumber}?tab=customerDetails`;
};

export const formatDate = (utcDate) => {
  return utcDate ? moment.utc(utcDate).format('DD/MM/YYYY') : null;
};

export const formatTime = (utcDate) => {
  return utcDate ? moment.utc(utcDate).format('HH:mm') + ' UTC' : null;
};

export const formatDateTime = (utcDate) => {
  return utcDate ? moment.utc(utcDate).format('DD/MM/YYYY HH:mm') + ' UTC' : null;
};

export const formatDateTimeLocal = (utcDate) => {
  return utcDate ? moment.utc(utcDate).local().format('DD/MM/YYYY HH:mm') : null;
};

export const convertDate = (date) => {
  if (date) {
    return `${date.getFullYear()}-${date.getMonth() < 9 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1}-${
      date.getDate() < 10 ? '0' + date.getDate() : date.getDate()
    }`;
  }

  return date;
};

export const convertDateWithTime = (date, isEndOfDay = false) => {
  if (date) {
    return `${date.getFullYear()}-${date.getMonth() < 9 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1}-${
      date.getDate() < 10 ? '0' + date.getDate() : date.getDate()
    }T${isEndOfDay ? '23:59:59' : '00:00:00'}`;
  }
  return date;
};

export const convertToFullDate = (dateStr) => {
  if (!dateStr) return;

  if (dateStr.includes('T')) {
    return new Date(dateStr);
  }

  const [year, month, day] = dateStr.split('-').map(Number);

  return new Date(year, month - 1, day); // months are 0-indexed
};

export const debounce = (func, delay = 400) => {
  let timeout;
  return function (...args) {
    const context = this;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = null;
      return func.apply(context, args);
    }, delay);
  };
};

export const MEDIA_QUERY = {
  HEADER_BUTTON_NAMES: '(min-width: 1050px)',
  DESKTOP: '(min-width: 769px)',
  NON_DESKTOP: '(min-width: 320px)',
  MOBILE: '(max-width: 499px)'
};

export const format = (val) => {
  const formatted = val?.split('_')?.join(' ')?.toLowerCase();

  return formatted?.charAt(0)?.toUpperCase() + formatted?.slice(1);
};

export const formatMoney = (value, precision = COMMON_CURRENCY_PRECISION) => {
  if (value === undefined || value === '' || value === null) {
    // eslint-disable-next-line no-console
    console.error('amountFormattedValue value:', value, typeof value);

    return '';
  }

  const numValue = Number(value);

  if (isNaN(numValue)) {
    // eslint-disable-next-line no-console
    console.error('amountFormattedValue value:', value, typeof value);

    return '';
  }

  let stringValue = numValue.toFixed(precision + 6);

  const dotIndex = stringValue.indexOf('.');
  if (dotIndex !== -1) {
    stringValue = stringValue.slice(0, dotIndex + precision + 1);
  }

  const formattedValue = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: precision,
    minimumFractionDigits: precision
  }).format(Number(stringValue));

  const [integerPart, fractionalPart] = formattedValue.split('.');

  if (!fractionalPart || fractionalPart.length < 2) {
    return formattedValue;
  }

  return `${integerPart}.${fractionalPart.replace(/0+$/, '').padEnd(2, '0')}`;
};

export const regularFieldRegExp = /^[a-zA-Z0-9!"#$%&'()*+,-./:;<=>?@[\]\\^_`{|}~\s]*$/;
export const countryCodeRegExp = /^[A-Z]+$/;

export const parseExternalAmlState = (amlState) => {
  if (!amlState) {
    return {
      statuses: '',
      tags: '',
      identifier: ''
    };
  }

  const { tags, initiated_at, approved_at, rejected_at, identifier } = amlState;
  const statuses = [];
  let parsedTags = '';
  let parsedStatuses = '';

  if (initiated_at) {
    statuses.push(
      i18n.getMessage('customers.externalAmlProvider.status.documentsRequested', {
        date: formatDate(initiated_at)
      })
    );
  }
  if (approved_at) {
    statuses.push(
      i18n.getMessage('customers.externalAmlProvider.status.approved', {
        date: formatDate(approved_at)
      })
    );
  }
  if (rejected_at) {
    statuses.push(
      i18n.getMessage('customers.externalAmlProvider.status.rejected', {
        date: formatDate(rejected_at)
      })
    );
  }

  if (tags) {
    parsedTags = tags.join(', ');
  }
  if (statuses?.length > 0) {
    parsedStatuses = statuses.join(', \n');
  }

  return {
    statuses: parsedStatuses,
    tags: parsedTags,
    identifier: identifier
  };
};

export const isObjectEmpty = (object) => {
  if (!object) return true;

  return Object.keys(object)?.length === 0;
};

export const convertMessageToHtmlAndTrimSpaces = (message) => {
  return message.toString('html').replace(/&nbsp;/g, '');
};

export const convertHtmlToString = (value) => {
  if (!value) return '';

  const cleanedString = value.toString('html').replace(/<[^>]+>/g, '');
  const tempElement = document.createElement('textarea');
  tempElement.innerHTML = cleanedString;

  return tempElement.value;
};

export const convertToTimeDate = (time) => {
  const parts = time.split(':');
  const date = new Date();
  date.setHours(parseInt(parts[0], 10), parseInt(parts[1], 10), parseInt(parts[2] || 0, 10), 0);

  return date;
};

export const convertDateToServerTimeString = (datetime) => {
  if (!datetime) {
    return datetime;
  }

  const dateObject = new Date(datetime);
  const hours = dateObject.getHours();
  const minutes = dateObject.getMinutes();

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
};

// Convert UTC time string to local Date object
export const convertUTCToLocalTime = (utcTimeString) => {
  if (!utcTimeString) {
    return null;
  }

  const parts = utcTimeString.split(':');
  const today = new Date();

  // Set UTC time
  today.setUTCHours(parseInt(parts[0], 10), parseInt(parts[1], 10), parseInt(parts[2] || 0, 10), 0);

  // Create a new Date object with local time
  const localDate = new Date();
  localDate.setHours(today.getHours(), today.getMinutes(), 0, 0);

  return localDate;
};

// Convert local Date object to UTC time string
export const convertLocalTimeToUTC = (localTime) => {
  if (!localTime) {
    return localTime;
  }

  const date = new Date(localTime);
  const localHours = date.getHours();
  const localMinutes = date.getMinutes();

  // Create a date with local time
  const today = new Date();
  today.setHours(localHours, localMinutes, 0, 0);

  // Get UTC time
  const utcHours = today.getUTCHours();
  const utcMinutes = today.getUTCMinutes();

  return `${utcHours.toString().padStart(2, '0')}:${utcMinutes.toString().padStart(2, '0')}:00`;
};

export const truncateFileName = (name, maxLength = 30) => {
  if (name?.length <= maxLength) return name;
  const start = name.slice(0, maxLength / 2 - 2);
  const end = name.slice(-maxLength / 2 + 2);
  return `${start}...${end}`;
};

export const getAccountTitle = (account) => {
  return account.transfer_provider === PAYMENT_PROVIDERS.WALLESTER
    ? i18n.getMessage('account.title.cardAccount', { accountNumber: account.wallet_number })
    : account.transfer_provider === PAYMENT_PROVIDERS.MANUAL ||
        account.transfer_provider === PAYMENT_PROVIDERS.DUKASCOPY ||
        account.transfer_provider === PAYMENT_PROVIDERS.CONVERA ||
        account.transfer_provider === PAYMENT_PROVIDERS.CONVERA_MERCHANT ||
        account.transfer_provider === PAYMENT_PROVIDERS.CONVERA_API
      ? i18n.getMessage('account.title.manual', { accountNumber: account.wallet_number })
      : account.multi_wallet_number
        ? account.multi_iban?.iban || account.multi_wallet_number
        : account.iban?.iban || account.wallet_number;
};

export const isCryptoProvider = (paymentProvider) => {
  return paymentProvider === PAYMENT_PROVIDERS.GRAPHEREX;
};

export const formatAccountNumber = (accountNumber) => {
  if (!accountNumber || accountNumber?.length < 12) {
    return accountNumber;
  }

  const firstPart = accountNumber.slice(0, 8);
  const lastPart = accountNumber.slice(-8);

  return `${firstPart}....${lastPart}`;
};

export const getAllCryptoCurrencies = (constants) => {
  if (!constants) {
    return [];
  }

  const providerProperties = constants.providerProperties.GRAPHEREX;
  if (!providerProperties) {
    return [];
  }

  const allCryptoNetworksCurrencies = Object.values(providerProperties).flatMap(
    (networkData) => networkData.currencies || []
  );

  return Array.from(new Set(allCryptoNetworksCurrencies));
};

export const getCryptoCurrenciesByNetwork = (network, constants) => {
  if (!constants?.providerProperties?.GRAPHEREX || !network) {
    return [];
  }
  const networksArray = Object.values(constants.providerProperties.GRAPHEREX);
  const networkData = networksArray.find((networkItem) => networkItem.code === network);

  return networkData?.currencies ? Array.from(new Set(networkData.currencies)) : [];
};

export const isCryptoCurrency = (currency, allCryptoCurrencies) => {
  if (!currency || !allCryptoCurrencies) {
    return false;
  }

  return allCryptoCurrencies.includes(currency);
};

export const getNetworksByCryptoCurrency = (currency, constants) => {
  if (!currency || !constants?.providerProperties?.GRAPHEREX) return [];

  return (
    Object.entries(constants.providerProperties.GRAPHEREX)
      // eslint-disable-next-line no-unused-vars
      .filter(([_, network]) => network?.currencies?.includes(currency))
      .map(([networkName]) => networkName)
  );
};

const getLimitKeyPartValue = (value) => (!value ? 'ANY' : value?.toUpperCase());

const limitObjectKeys = ['limitType', 'transactionType', 'period', 'transferProvider', 'transferType', 'network'];

const getLimitKeyParts = (limit) => limitObjectKeys.map((key) => getLimitKeyPartValue(limit[key]));

export const getLimitMessageKey = (limit) => getLimitKeyParts(limit).join('_');

export const getLimitInfo = (limit) =>
  limitObjectKeys.reduce((info, key) => {
    const value = limit[key];
    if ((value !== undefined && value !== null) || (value === null && key === 'transferProvider')) {
      info[key] = getLimitKeyPartValue(value);
    }
    return info;
  }, {});

export const isPositiveNumber = (value) => value && !isNaN(value) && parseFloat(value) >= 0;

export const momentFromDateAndTime = (day, hour, minute, timezone) => {
  const date = new Date();

  day = day === 'L' ? moment().endOf('month').date() : Number(day);

  return moment.tz(`${date.getFullYear()}-${date.getMonth() + 1}-${day} ${hour}:${minute}`, 'YYYY-MM-D h:m', timezone);
};

// Human-readable labels for known "fields" entries in validation error responses.
// Falls back to a humanized version of the raw field name for anything not listed here.
const SERVER_FIELD_NAME_LABELS = {
  from_date: 'From date',
  to_date: 'To date'
};

// Human-readable phrasing for known per-field validation codes.
// Falls back to a generic "has an invalid value" for anything not listed here.
const SERVER_FIELD_CODE_PHRASES = {
  Required: 'is required',
  NotBlank: 'is required',
  NotNull: 'is required',
  NotEmpty: 'is required'
};

const humanizeServerFieldName = (field) => {
  const lastSegment = field.split('.').pop();
  const label = SERVER_FIELD_NAME_LABELS[lastSegment];

  if (label) return label;

  const withSpaces = lastSegment.replace(/_/g, ' ');
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
};

const buildServerFieldMessage = (field, code) =>
  `${humanizeServerFieldName(field)} ${SERVER_FIELD_CODE_PHRASES[code] || 'has an invalid value'}`;

export const parseServerError = (error) => {
  // Check if error has details array with validation errors
  if (
    error &&
    error?.response?.body?.code === 'ARGUMENT_NOT_VALID' &&
    Array.isArray(error?.response?.body?.details) &&
    error?.response?.body?.details?.length > 0
  ) {
    // Return first error message from details array
    return error?.response?.body?.details[0]?.message || 'Invalid value';
  }

  // Special handling for specific error codes
  if (error?.response?.body?.code === 'ACCOUNT_REPORT_TOO_LARGE') {
    return (
      error?.response?.body?.message ||
      'Report contains too many transactions. Please narrow the date range or wallet selection.'
    );
  }

  // Generic per-field validation errors, e.g. { code: 'MISSING_PARAMETER', fields: [{ field, code, message }] }
  if (error && Array.isArray(error?.response?.body?.fields) && error?.response?.body?.fields?.length > 0) {
    const fields = error.response.body.fields;
    const fieldNames = fields.map((field) => field.field);

    // Friendlier combined message for the most common case: an empty date range filter
    if (fieldNames.includes('from_date') && fieldNames.includes('to_date')) {
      return 'From/To dates are missing';
    }

    const messages = fields
      .map((field) => field.message || buildServerFieldMessage(field.field, field.code))
      .filter(Boolean);

    if (messages.length > 0) {
      return messages.join(', ');
    }
  }

  // Return error message from response body or default message
  return (
    error?.response?.body?.message ||
    error?.response?.body?.error_description ||
    error?.message ||
    'An unknown error occurred'
  );
};

export const parseDateFromServer = (date) => {
  if (!date) return '';

  // Convert date from YYYY-MM-DD format to DD/MM/YYYY format
  if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = date.split('-');
    return `${day}/${month}/${year}`;
  }

  return date;
};

export const parseDateToServer = (date) => {
  if (!date) return '';

  // Convert date from DD/MM/YYYY format to YYYY-MM-DD format
  if (date.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    const [day, month, year] = date.split('/');
    return `${year}-${month}-${day}`;
  }

  return date;
};

export const prepareFieldErrors = (i18n, error, variable, isRegistration) => {
  let fieldsError = {};

  if (error?.details && Array.isArray(error?.details)) {
    error.details.forEach((detail) => {
      const fieldName = detail?.field.split('.').pop();

      if (detail?.code) {
        // fieldsError[fieldName] = i18n.getMessage(`error.fieldError.${detail?.field}.${detail?.code}`, {
        //   variable
        // });
        fieldsError[fieldName] = detail?.message;
      } else {
        fieldsError[fieldName] = detail?.message;
      }
    });
  }

  if (
    error?.fields &&
    error?.fields[0]?.code &&
    (error?.fields[0]?.code === 'NO_PAYMENT_METHODS_AVAILABLE' ||
      error?.fields[0]?.code === 'EXTERNAL_IBAN_VALIDATION_FAILED')
  ) {
    fieldsError = { iban: i18n.getMessage(`error.fieldError.${error?.fields[0].field}.${error?.fields[0].code}`) };
  } else {
    error?.fields &&
      Object.keys(error.fields).map((field) => {
        if (isRegistration && field === 'password' && error.fields[field] === 'Password') return;
        // eslint-disable-next-line max-len
        fieldsError[field.split('.').pop()] = i18n.getMessage(`error.fieldError.${field}.${error.fields[field]}`, {
          variable
        });
      });
  }

  return fieldsError;
};

// Transaction display helpers
export const getTransactionDirectionIcon = (transactionType) => {
  return transactionType === TRANSACTION_TYPES.INCOMING ? '↓' : '↑';
};

export const getTransactionDirectionClass = (transactionType) => {
  return transactionType === TRANSACTION_TYPES.INCOMING
    ? 'transaction-direction-incoming'
    : 'transaction-direction-outgoing';
};

export const isExchangeTransaction = (transferType) => {
  return transferType === TRANSFER_TYPE.EXCHANGE;
};

export const getTransactionStatusClass = (status) => {
  switch (status) {
    case TRANSACTION_STATUS.PAID:
      return 'transaction-status-paid';
    case TRANSACTION_STATUS.FAILED:
    case TRANSACTION_STATUS.REJECTED:
      return 'transaction-status-failed';
    case TRANSACTION_STATUS.PENDING:
      return 'transaction-status-pending';
    default:
      return '';
  }
};

export const getPaymentMethodClass = (paymentMethod) => {
  return paymentMethod === PAYMENT_METHOD.CRYPTO ? 'payment-method-crypto' : '';
};

/*
 * Sanitizes text from ChatGPT and other external sources
 * Converts problematic Unicode characters to safe alternatives
 * @param {string} text - Input text to sanitize
 * @returns {string} - Sanitized text
 */
export const sanitizeMessageText = (text) => {
  if (!text) return text;

  // Replace common problematic characters with safe alternatives
  const replacements = {
    '\u00A0': ' ', // Non-breaking space -> regular space
    '\u2028': '\n', // Line separator -> newline
    '\u2029': '\n', // Paragraph separator -> newline
    '\u201C': '"', // Left double quotation mark
    '\u201D': '"', // Right double quotation mark
    '\u2018': "'", // Left single quotation mark
    '\u2019': "'", // Right single quotation mark
    '\u2013': '-', // En dash
    '\u2014': '-', // Em dash
    '\u2026': '...', // Horizontal ellipsis
    '\u2022': '*', // Bullet point
    '\u2023': '>', // Triangular bullet
    '\u25E6': 'o', // White bullet
    '\u2043': '-', // Hyphen bullet
    '\u2219': '*' // Bullet operator
  };

  let sanitized = text;
  for (const [char, replacement] of Object.entries(replacements)) {
    sanitized = sanitized.replace(new RegExp(char, 'g'), replacement);
  }

  // Remove zero-width characters
  sanitized = sanitized
    .replace(/\u200B/g, '') // Zero-width space
    .replace(/\uFEFF/g, '') // Zero-width no-break space
    .replace(/\u200C/g, '') // Zero-width non-joiner
    .replace(/\u200D/g, ''); // Zero-width joiner

  // Remove control characters except tab, newline, and carriage return
  // eslint-disable-next-line no-control-regex
  sanitized = sanitized.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');

  return sanitized;
};

export const formatDateForDocuments = (date) => {
  return momentLib(date).format('DD.MM.YYYY');
};
